import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import { LinkTo } from '@ember/routing';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import fileQueue from 'ember-file-upload/helpers/file-queue';
import FileDropzone from 'ember-file-upload/components/file-dropzone';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import Chip from 'tio-ui/components/chip';
import Modal from 'tio-ui/components/modal';
import RadioGroup from 'tio-ui/components/radio-group';
import Text from 'tio-ui/components/text';
import { not } from 'tio-ui/utilities';
import { presentationForSlrLoanStanding } from 'tio-employee/utils/slr-loan-standing-verification';
import { loanStandingGuidelines } from 'tio-employee/utils/slr-plan-eligibility';
import {
  PhCaretDown,
  PhCaretUp,
  PhCheckCircle,
  PhSealWarning,
  PhSparkle,
  PhWarningCircle,
  PhXCircle,
} from 'ember-phosphor-icons';
import type { TOC } from '@ember/component/template-only';
import type { IntlService } from 'ember-intl';
import type { UploadFile, Queue } from 'ember-file-upload';
import type FileQueueService from 'ember-file-upload/services/file-queue';
import type SlrLoanStandingVerificationService from 'tio-employee/services/slr-loan-standing-verification';
import type { SlrLoanStandingAuditState } from 'tio-employee/types/slr-loan-standing-verification';

interface LoanStandingAttentionCardSignature {
  Args: {
    state: SlrLoanStandingAuditState;
    /** Refreshes the route-level audit state after a live upload settles — the card variant copy, attention count, and success section all read the refetched state. */
    onRefresh?: () => Promise<void> | void;
    /** Records the participant's answer from the inline Review Name panel (fuzzy name match). Resolves once the settled state is applied. */
    onConfirmName?: (confirmed: boolean) => Promise<void> | void;
    /** The plan's SELECTED eligibility-condition names (tio-admin). No longer varies the checklist (QA 8/1: fixed designed copy) but stays wired for per-plan asks that may return. */
    eligibilityConditions?: readonly string[] | null;
    /** Test hook: shortens the lost-broadcast fallback window. */
    verdictTimeoutMs?: number;
  };
  Element: HTMLDivElement;
}

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
// Both are in the backend's ObservableAsset::ALLOWED_CONTENT_TYPES; drag-and-drop bypasses the file input's `accept` hint, so this list also drives handleFileAdded validation.
const VALID_EXTENSIONS = ['pdf', 'txt'];
const ACCEPTED_FILE_TYPES = VALID_EXTENSIONS.map((extension) => `.${extension}`).join(',');

// Reasons already rendered through a richer variant (mismatch callout / Review Name panel) — the generic flagged banner would double up on them.
const BANNER_EXEMPT_REASONS = new Set(['name_mismatch', 'name_match_confirmation_required']);

// Full-sentence banner ask (Figma #76): the document was fine — the ACCOUNT it shows is behind.
const PAST_DUE_REASONS = new Set([
  'status_not_in_good_standing',
  'delinquent_or_collections',
  'past_due',
]);

interface UploadFeedbackDisplay {
  filename: string;
  messageKey: string;
  intent: 'danger' | 'warning';
  leadWithFilename: boolean;
  /** Stale-NSLDS strips carry the "no payments currently due?" link (Figma 767-18351) to the leave-status pop-up. */
  showLeaveStatusLink?: boolean;
}

// TA's FlaggedBanner recipe (categorized-upload/flagged-banner.gts): left accent, soft wash, flex-wrap body; filename renders via Glimmer interpolation only, never htmlSafe — it's user-controlled.
const UploadFeedbackBanner: TOC<{
  Args: {
    feedback: UploadFeedbackDisplay;
    onDismiss: () => void;
    onOpenLeaveStatus: () => void;
  };
}> = <template>
  <div
    class="tio-row-top border-l-4 px-4 py-3 last:rounded-b-xl
      {{if
        (eq @feedback.intent 'warning')
        'tio-border-warning tio-bg-warning-soft'
        'tio-border-danger tio-bg-danger-soft'
      }}"
    role="alert"
    data-test-slr-loan-standing-upload-feedback={{@feedback.intent}}
  >
    <PhWarningCircle
      @size={{18}}
      @weight="fill"
      class="mt-0.5 shrink-0
        {{if (eq @feedback.intent 'warning') 'tio-fg-warning' 'tio-fg-danger'}}"
    />
    <div class="tio-col-extra-tight tio-fill-min text-sm">
      <div class="tio-row-snug flex-wrap">
        {{#if @feedback.leadWithFilename}}
          <span
            class="font-medium
              {{if (eq @feedback.intent 'warning') 'tio-fg-warning-h' 'tio-fg-danger-h'}}"
          >{{@feedback.filename}}</span>
        {{/if}}
        <span
          class={{if (eq @feedback.intent "warning") "tio-fg-warning-body" "tio-fg-danger-body"}}
        >
          {{t @feedback.messageKey}}
        </span>
        {{#if @feedback.showLeaveStatusLink}}
          <Button
            @appearance="minimal"
            @size="sm"
            class="tio-text-sm-medium underline whitespace-nowrap
              {{if (eq @feedback.intent 'warning') 'tio-fg-warning-h' 'tio-fg-danger-h'}}"
            data-test-slr-loan-standing-leave-status-link
            {{on "click" @onOpenLeaveStatus}}
          >
            {{t "slr.loan_standing.attention.leave_status_link"}}
          </Button>
        {{/if}}
      </div>
    </div>
    <Button
      @appearance="minimal"
      @size="sm"
      @ariaLabel={{t "slr.loan_standing.attention.flagged_dismiss"}}
      class="shrink-0 {{if (eq @feedback.intent 'warning') 'tio-fg-warning-h' 'tio-fg-danger-h'}}"
      data-test-slr-loan-standing-upload-feedback-dismiss
      {{on "click" @onDismiss}}
    >
      <PhXCircle @size={{16}} />
    </Button>
  </div>
</template>;

// Switches the instruction panel to the NSLDS second-document variant: the first document read fine but reported a federal non-repayment status (e.g. deferment), so only an official NSLDS file can verify the account.
const NSLDS_REASONS = new Set(['nslds_required_for_federal_non_repayment_status']);

/** Parses date-only values (YYYY-MM-DD) as LOCAL dates — `new Date('2026-12-01')` is UTC midnight and renders the previous day/month in western timezones. Null for unparseable input. */
function parseLocalish(value: string | null | undefined) {
  if (!value) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const parsed = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** The SLR "Needs Your Attention" card (Figma: SLR // Loan Standing Verification). Scoped upstream to the contribution-receiving account only — never sees other accounts' standing. */
export default class LoanStandingAttentionCard extends Component<LoanStandingAttentionCardSignature> {
  @service declare intl: IntlService;
  @service declare fileQueue: FileQueueService;
  @service('slr-loan-standing-verification')
  declare slrLoanStandingVerification: SlrLoanStandingVerificationService;

  @tracked isExpanded = true;

  #queue: Queue | null = null;

  // A method, not a getter: memoizing lazily is a side effect eslint bans from computed properties.
  #ensureQueue(): Queue {
    this.#queue ??= this.fileQueue.findOrCreate('slr-loan-standing-inline');
    return this.#queue;
  }

  // Pre-submit validation failure (bad extension / oversized) — rendered in the same flagged-banner slot as verdict failures.
  @tracked localFileError: { filename: string; messageKey: string } | null = null;

  #fileInputElement: HTMLInputElement | null = null;

  /** The hidden file input carries `selectFile`; the visible `<Button>` can't live inside a `<label>` (interactive content doesn't forward label activation), so it opens the picker programmatically. */
  registerFileInput = modifier((element: HTMLInputElement) => {
    this.#fileInputElement = element;
    return () => {
      this.#fileInputElement = null;
    };
  });

  openFilePicker = () => {
    // Allow re-selecting the same file after a dismissed failure — a stale value would swallow the change event.
    if (this.#fileInputElement) this.#fileInputElement.value = '';
    this.#fileInputElement?.click();
  };

  get liveUpload() {
    return this.slrLoanStandingVerification.liveUpload;
  }

  /** Other-account verdicts redirected from THIS upload (#9972): names the institution (or a generic fallback) plus the mapped description; renders regardless of whether the card's OWN verdict has settled — the broadcasts arrive in arbitrary order. */
  get redirectedVerdictDisplays() {
    return this.liveUpload.redirectedVerdicts.map((verdict) => {
      const description = this.intl.t(presentationForSlrLoanStanding(verdict.state).descriptionKey);
      return verdict.financialInstitutionName
        ? this.intl.t('slr.loan_standing.attention.different_account_verdict', {
            institution: verdict.financialInstitutionName,
            description,
          })
        : this.intl.t('slr.loan_standing.attention.different_account_verdict_generic', {
            description,
          });
    });
  }

  get isUploadBusy() {
    return this.slrLoanStandingVerification.isLiveUploadBusy;
  }

  /** Reflection landed; only the eligibility evaluation remains. */
  get isVerifyingPhase() {
    return this.liveUpload.phase === 'verifying';
  }

  /** Border treatment mirroring the TA categorized-upload row (document-row.gts `borderClass`): analyzing → brand, flagged/error → red, timeout → amber, otherwise neutral. */
  get docItemBorderClass() {
    if (this.localFileError) return 'tio-border-danger';
    if (this.isUploadBusy) return 'app-border-brand';
    const { phase } = this.liveUpload;
    if (phase === 'error') return 'tio-border-danger';
    if (phase === 'timeout') return 'app-border-warning';
    if (phase === 'settled' && this.uploadFeedback) return 'tio-border-danger';
    return 'tio-border-subtle';
  }

  /** The named queue, exposed for the drop target (same queue the picker feeds). */
  get dropzoneQueue() {
    return this.#ensureQueue();
  }

  handleFileAdded = (uploadFile: UploadFile) => {
    // isProcessing (not isUploadBusy): a route-level 'processing' status also hides the picker while THIS session's service is idle, and the dropzone stays live in both cases.
    if (this.isProcessing) {
      this.#ensureQueue().remove(uploadFile);
      return;
    }
    this.localFileError = null;
    this.slrLoanStandingVerification.dismissLiveUpload();
    const file = uploadFile.file;

    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!VALID_EXTENSIONS.includes(extension)) {
      this.localFileError = {
        filename: file.name,
        messageKey: 'slr.loan_standing.attention.flagged_invalid_type',
      };
      this.#ensureQueue().remove(uploadFile);
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.localFileError = {
        filename: file.name,
        messageKey: 'slr.loan_standing.attention.flagged_too_large',
      };
      this.#ensureQueue().remove(uploadFile);
      return;
    }

    const employeePlanAccountId = this.args.state.employeePlanAccountId;
    if (!employeePlanAccountId) {
      this.#ensureQueue().remove(uploadFile);
      return;
    }

    // Selecting the file IS the submission (Figma 767-7670); the queue entry releases once the create chain consumes the File — the named queue is session-scoped and would otherwise retain every selection.
    void this.slrLoanStandingVerification
      .submitVerificationDocument(employeePlanAccountId, uploadFile, {
        verdictTimeoutMs: this.args.verdictTimeoutMs,
        onSettled: () => void this.args.onRefresh?.(),
      })
      .finally(() => this.#ensureQueue().remove(uploadFile));
  };

  dismissUploadFeedback = () => {
    this.localFileError = null;
    this.slrLoanStandingVerification.dismissLiveUpload();
  };

  /** One slot for pre-submit validation, chain failures, unreadable documents, failed verdicts, and timeout; `leadWithFilename` mirrors the mockups (file-shaped problems lead with the filename, verdict-shaped ones are full sentences — Figma #76). Reasons with richer card treatments are exempt. */
  get uploadFeedback(): UploadFeedbackDisplay | null {
    if (this.localFileError) {
      return { ...this.localFileError, intent: 'danger', leadWithFilename: true };
    }

    const { phase, filename, outcome, errorKind } = this.liveUpload;
    if (!filename) return null;

    if (phase === 'error') {
      const messageKey =
        errorKind === 'invalid_document'
          ? 'slr.loan_standing.attention.flagged_unreadable'
          : errorKind === 'processing_failed'
            ? 'slr.loan_standing.attention.flagged_processing_failed'
            : 'slr.loan_standing.attention.flagged_upload_failed';
      return { filename, messageKey, intent: 'danger', leadWithFilename: true };
    }

    if (phase === 'timeout') {
      return {
        filename,
        messageKey: 'slr.loan_standing.attention.flagged_still_processing',
        intent: 'warning',
        leadWithFilename: true,
      };
    }

    if (phase === 'settled' && outcome && outcome.status !== 'eligible') {
      const reason = outcome.primaryReason ?? '';
      if (BANNER_EXEMPT_REASONS.has(reason)) return null;
      if (reason === 'stale_document' || reason === 'outside_audit_window') {
        // NSLDS exports (.txt) age by "balance as of"/"status change" dates, so the strip links the leave-status pop-up (Figma 767-18351); keyed on the FILE, not the card variant, so a stale PDF in the NSLDS variant never gets MyStudentData copy.
        const isNsldsUpload = filename.toLowerCase().endsWith('.txt');
        return {
          filename,
          messageKey: isNsldsUpload
            ? 'slr.loan_standing.attention.flagged_stale_nslds'
            : 'slr.loan_standing.attention.flagged_stale',
          intent: 'danger',
          leadWithFilename: true,
          showLeaveStatusLink: isNsldsUpload,
        };
      }
      if (reason === 'document_for_different_account') {
        // Cross-servicer upload (#9972): the document reflected onto a different account, so this EPA's verdict deliberately didn't change — say so honestly instead of a false stale/45-day error.
        return {
          filename,
          messageKey: 'slr.loan_standing.attention.flagged_different_account',
          intent: 'warning',
          leadWithFilename: true,
        };
      }
      if (PAST_DUE_REASONS.has(reason)) {
        return {
          filename,
          messageKey: 'slr.loan_standing.attention.flagged_past_due',
          intent: 'danger',
          leadWithFilename: false,
        };
      }
      return {
        filename,
        messageKey: 'slr.loan_standing.attention.flagged_generic',
        intent: 'danger',
        leadWithFilename: true,
      };
    }

    return null;
  }

  // Leave-status pop-up (Figma 767-20511), opened from the stale-NSLDS strip.
  @tracked isLeaveStatusModalOpen = false;

  openLeaveStatusModal = () => {
    this.isLeaveStatusModalOpen = true;
  };

  closeLeaveStatusModal = () => {
    this.isLeaveStatusModalOpen = false;
  };

  // Inline Review Name panel (Figma v3 node 1333-3886): the confirm CTA opens it in place of the document row.
  @tracked isReviewPanelOpen = false;
  @tracked reviewAnswer: 'yes' | 'no' | undefined;

  get reviewOptions() {
    return [
      { key: 'yes', label: this.intl.t('slr.loan_standing.drawer.confirm_name_yes') },
      { key: 'no', label: this.intl.t('slr.loan_standing.drawer.confirm_name_no') },
    ];
  }

  openReviewPanel = () => {
    this.reviewAnswer = undefined;
    this.isReviewPanelOpen = true;
  };

  closeReviewPanel = () => {
    this.isReviewPanelOpen = false;
    this.reviewAnswer = undefined;
  };

  setReviewAnswer = (value: 'yes' | 'no') => {
    this.reviewAnswer = value;
  };

  get isConfirmDisabled() {
    return !this.reviewAnswer || this.confirmSelection.isRunning;
  }

  confirmSelection = dropTask(async () => {
    if (!this.reviewAnswer) return;
    await this.args.onConfirmName?.(this.reviewAnswer === 'yes');
    this.closeReviewPanel();
  });

  /** The confirm variant opens the inline panel; every other ask opens the file picker. */
  get primaryCtaAction() {
    return this.isNameConfirmVariant ? this.openReviewPanel : this.openFilePicker;
  }

  get isNsldsVariant() {
    return NSLDS_REASONS.has(this.args.state.primaryReason ?? '');
  }

  /** Fuzzy name-match (tio-api#8054): the borrower name is only CLOSE, so the ask is a yes/no confirmation, not another upload — the card swaps subtitle/CTA copy and drops the checklist. */
  get isNameConfirmVariant() {
    return this.args.state.action === 'confirm_name_match';
  }

  /** States offering no upload (confirm-name, hard-ineligible) explain themselves via the reason's mapped description instead of the generic upload pitch, and drop the checklist. */
  get showReasonSubtitle() {
    return this.isNameConfirmVariant || this.showContactSupportAction;
  }

  get reasonDescriptionKey() {
    return presentationForSlrLoanStanding(this.args.state).descriptionKey;
  }

  /**
   * A specific-ask reason that KEEPS the checklist (unlike showReasonSubtitle): post-NSLDS still asks for a statement, so a generic "upload a loan document" subtitle would read as a first-time ask.
   * A hard name mismatch is likewise a retryable document problem (#10017) — it keeps the upload chrome instead of dead-ending in the ineligible verdict.
   */
  get showRecoverableReasonSubtitle() {
    return (
      this.args.state.primaryReason === 'statement_required_for_repayment_status' ||
      this.isNameMismatchVariant
    );
  }

  /** Hard name mismatch — retryable upload ask with an inline support link. */
  get isNameMismatchVariant() {
    return this.args.state.primaryReason === 'name_mismatch';
  }

  get showInstructionPanel() {
    return !this.showReasonSubtitle;
  }

  /** The fixed designed checklist (Figma 767-18351; QA 8/1 ruling). */
  get guidelines() {
    return loanStandingGuidelines(this.args.eligibilityConditions);
  }

  get uploadCtaKey() {
    return this.isNameConfirmVariant
      ? 'slr.loan_standing.actions.confirm_name_match'
      : 'slr.loan_standing.actions.upload_document';
  }

  /** Audit-month presentation (Figma: Notification/Audit Month) — active only once the backend supplies audit-window context (RFC 012), dark everywhere until then. */
  get hasAuditContext() {
    const { auditRequiredBy, auditPastDue, contributionPeriodEnd } = this.args.state;
    return !!(auditRequiredBy || auditPastDue || contributionPeriodEnd);
  }

  get titleKey() {
    // Figma v3 (node 767-7670): name-match leads with the problem; past-due leads with unblocking; everything else uses the short ask.
    if (this.isNameConfirmVariant) return 'slr.loan_standing.attention.name_confirm_title';
    // Hard-ineligible is a statement, not a task — never the "Verify…!" ask or the past-due unblock pitch.
    if (this.showContactSupportAction) return 'slr.loan_standing.attention.ineligible_title';
    return this.args.state.auditPastDue
      ? 'slr.loan_standing.attention.past_due_title'
      : 'slr.loan_standing.attention.title';
  }

  /** "…through December" period label. Date-only strings parse as LOCAL dates — `new Date('2026-12-01')` is UTC midnight and renders the PREVIOUS month in western timezones. */
  get contributionPeriodEndMonth() {
    const parsed = parseLocalish(this.args.state.contributionPeriodEnd);
    return parsed && this.intl.formatDate(parsed, { month: 'long' });
  }

  /** "January 2027" — the success card's forward-looking reminder (QA page-states, Gtg row). */
  get nextAuditMonthDisplay() {
    const parsed = parseLocalish(this.args.state.nextAuditStartsOn);
    return parsed && this.intl.formatDate(parsed, { month: 'long', year: 'numeric' });
  }

  /** Start of the still-unlockable window ("…for August–December", Figma 767-16825): the month after the deadline. Null when unparseable, falling the subtitle back to its generic form. */
  get contributionPeriodStartMonth() {
    const parsed = parseLocalish(this.args.state.auditRequiredBy);
    if (!parsed) return null;
    const nextMonth = new Date(parsed.getFullYear(), parsed.getMonth() + 1, 1);
    return this.intl.formatDate(nextMonth, { month: 'long' });
  }

  /** Whole calendar days to the deadline for the "due in N days" chip; null when past/absent/unparseable — past-due renders its own chip, unparseable falls back to required_by. */
  get dueInDays() {
    const parsed = parseLocalish(this.args.state.auditRequiredBy);
    if (!parsed) return null;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const days = Math.round((parsed.getTime() - startOfToday.getTime()) / 86_400_000);
    return days >= 0 ? days : null;
  }

  /** Template guard: 0 ("due in 0 days") is a valid chip, not a fallthrough. */
  get hasDueInDays() {
    return this.dueInDays !== null;
  }

  /** Short localized deadline for the required-by chip; falls back to the raw string for unparseable input rather than hiding a deadline the backend asserted. */
  get auditRequiredByLabel() {
    const raw = this.args.state.auditRequiredBy;
    if (!raw) return null;
    const parsed = parseLocalish(raw);
    return parsed ? this.intl.formatDate(parsed, { month: '2-digit', day: '2-digit' }) : raw;
  }

  get isProcessing() {
    // Busy live upload or a route-level processing status — either way the row shows the analyzing chip instead of the button.
    return this.isUploadBusy || this.args.state.status === 'processing';
  }

  get isVerified() {
    return this.args.state.status === 'eligible';
  }

  get documentLabelKey() {
    return this.isNsldsVariant
      ? 'slr.loan_standing.attention.nslds_file'
      : 'slr.loan_standing.attention.loan_verification_document';
  }

  /** The confirm variant isn't asking for a document — the borrower name just needs a yes/no — so it reuses the drawer's confirmation question instead of a document name. */
  get actionRowLabelKey() {
    return this.isNameConfirmVariant
      ? 'slr.loan_standing.drawer.confirm_name_prompt'
      : this.documentLabelKey;
  }

  /** Opens for actions it can actually serve (uploads + name confirmation); hard-ineligible states map to contact_support instead — never a dead-end drawer whose uploader is disabled. */
  get showUploadAction() {
    return ['upload_document', 'upload_newer_document', 'confirm_name_match'].includes(
      this.args.state.action
    );
  }

  get showContactSupportAction() {
    return this.args.state.action === 'contact_support';
  }

  toggleExpanded = () => {
    this.isExpanded = !this.isExpanded;
  };

  <template>
    {{#if this.isVerified}}
      <Card @padding="md" data-test-slr-loan-standing-success ...attributes>
        <div class="tio-col-tight">
          <Text @weight="semibold">{{t "slr.loan_standing.attention.success_title"}}</Text>
          <Text @size="sm" @variant="muted">
            {{#if this.contributionPeriodEndMonth}}
              {{t
                "slr.loan_standing.attention.success_body_with_period"
                periodEnd=this.contributionPeriodEndMonth
              }}
            {{else}}
              {{t "slr.loan_standing.attention.success_body"}}
            {{/if}}
          </Text>
          {{#if this.nextAuditMonthDisplay}}
            <Text @size="sm" @variant="muted" data-test-slr-loan-standing-success-next-audit>
              {{t
                "slr.loan_standing.attention.success_next_audit"
                month=this.nextAuditMonthDisplay
              }}
            </Text>
          {{/if}}
        </div>
      </Card>
    {{else}}
      <Card @padding="none" data-test-slr-loan-standing-attention-card ...attributes>
        <div class="tio-loan-standing-attention p-4">
          <div class="tio-loan-standing-attention__header">
            <span class="tio-loan-standing-attention__icon">
              <PhSealWarning @size={{24}} @weight="duotone" />
            </span>
            <div class="tio-col-tight flex-1">
              <div class="tio-row-tight flex-wrap">
                <Text @weight="semibold" data-test-slr-loan-standing-attention-title>
                  {{t this.titleKey}}
                </Text>
                {{! Deadline chips never render for a hard-ineligible verdict — that would imply a fixable deadline no upload can meet (#9887 follow-up). }}
                {{#unless this.showContactSupportAction}}
                  {{#if @state.auditPastDue}}
                    <Chip
                      @intent="danger"
                      @appearance="faded"
                      @size="sm"
                      data-test-slr-loan-standing-past-due
                    >
                      {{t "slr.loan_standing.attention.past_due"}}
                    </Chip>
                  {{else if this.hasDueInDays}}
                    <Chip
                      @intent="warning"
                      @appearance="faded"
                      @size="sm"
                      data-test-slr-loan-standing-due-in
                    >
                      {{t "slr.loan_standing.attention.due_in_days" days=this.dueInDays}}
                    </Chip>
                  {{else if this.auditRequiredByLabel}}
                    <Chip
                      @intent="danger"
                      @appearance="faded"
                      @size="sm"
                      data-test-slr-loan-standing-required-by
                    >
                      {{t "slr.loan_standing.attention.required_by" date=this.auditRequiredByLabel}}
                    </Chip>
                  {{/if}}
                {{/unless}}
              </div>
              <Text @size="sm" @variant="muted">
                {{#if (or this.showReasonSubtitle this.showRecoverableReasonSubtitle)}}
                  {{t this.reasonDescriptionKey}}
                  {{#if this.isNameMismatchVariant}}
                    {{! The mismatch error links "contact support" inline (Figma 767:20046); the description key ends mid-clause so the link carries the verb phrase in every locale. }}
                    <LinkTo
                      @route="authenticated.contact-us"
                      class="tio-fg-info hover:tio-fg-info-body underline"
                      data-test-slr-loan-standing-mismatch-support-link
                    >{{t "slr.loan_standing.attention.contact_support_inline"}}</LinkTo>.
                  {{/if}}
                {{else if @state.auditPastDue}}
                  {{#if this.contributionPeriodEndMonth}}
                    {{t
                      "slr.loan_standing.attention.past_due_subtitle"
                      periodEnd=this.contributionPeriodEndMonth
                    }}
                  {{else}}
                    {{t "slr.loan_standing.attention.subtitle"}}
                  {{/if}}
                {{else if (and this.contributionPeriodStartMonth this.contributionPeriodEndMonth)}}
                  {{t
                    "slr.loan_standing.attention.subtitle_with_period"
                    periodStart=this.contributionPeriodStartMonth
                    periodEnd=this.contributionPeriodEndMonth
                  }}
                {{else}}
                  {{t "slr.loan_standing.attention.subtitle"}}
                {{/if}}
              </Text>
            </div>
            {{#if this.showInstructionPanel}}
              <Button
                @appearance="minimal"
                @size="sm"
                @ariaLabel={{t
                  (if
                    this.isExpanded
                    "slr.loan_standing.attention.collapse"
                    "slr.loan_standing.attention.expand"
                  )
                }}
                aria-expanded="{{this.isExpanded}}"
                data-test-slr-loan-standing-attention-toggle
                {{on "click" this.toggleExpanded}}
              >
                {{#if this.isExpanded}}
                  <PhCaretUp @size={{18}} />
                {{else}}
                  <PhCaretDown @size={{18}} />
                {{/if}}
              </Button>
            {{/if}}
          </div>

          {{#if (and this.isExpanded this.showInstructionPanel)}}
            <div
              class="tio-loan-standing-attention__panel"
              data-test-slr-loan-standing-instructions
            >
              {{#if this.isNsldsVariant}}
                <Text @size="sm">
                  <strong>{{t "slr.loan_standing.attention.nslds_intro_lead"}}</strong>
                  {{t "slr.loan_standing.attention.nslds_intro_rest"}}
                </Text>
                <ol class="tio-loan-standing-attention__steps">
                  <li>
                    <strong>{{t "slr.loan_standing.attention.nslds_step_login_label"}}</strong>
                    {{t "slr.loan_standing.attention.nslds_step_login"}}
                  </li>
                  <li>
                    <strong>{{t "slr.loan_standing.attention.nslds_step_download_label"}}</strong>
                    {{t "slr.loan_standing.attention.nslds_step_download"}}
                  </li>
                  <li>
                    <strong>{{t "slr.loan_standing.attention.nslds_step_upload_label"}}</strong>
                    {{t "slr.loan_standing.attention.nslds_step_upload"}}
                  </li>
                </ol>
              {{else}}
                <Text @size="sm" @weight="semibold">
                  {{t "slr.loan_standing.attention.checklist_heading"}}
                </Text>
                <ol class="tio-loan-standing-attention__steps">
                  {{#each this.guidelines as |guideline|}}
                    <li data-test-slr-loan-standing-guideline={{guideline.condition}}>
                      <strong>{{t guideline.labelKey}}</strong>
                      {{t guideline.textKey}}
                    </li>
                  {{/each}}
                </ol>
              {{/if}}
              <Text @size="sm">
                <strong>{{t "slr.loan_standing.attention.note_label"}}</strong>
                {{t "slr.loan_standing.attention.note"}}
              </Text>
            </div>
          {{/if}}

          <div class="tio-col-tight">
            {{#if this.isNameConfirmVariant}}
              {{#if @state.sourceDocumentFilename}}
                {{! Red file callout (name-match frames 1333/1339): names the reviewed document above the Review panel. }}
                <div
                  class="tio-bg-danger-soft border-l-4 tio-border-danger rounded-r-lg px-3 py-2 tio-row-tight"
                  data-test-slr-name-mismatch-file
                >
                  <PhWarningCircle @size={{14}} class="tio-fg-danger shrink-0" />
                  {{! The filename is user-controlled — it must pass through Glimmer interpolation, never htmlSafe, or a markup-shaped filename would parse as HTML. }}
                  <Text @size="xs" class="tio-fg-danger-h">
                    {{t "slr.loan_standing.attention.name_mismatch_file_prefix"}}
                    <strong>{{@state.sourceDocumentFilename}}</strong>
                    {{t "slr.loan_standing.attention.name_mismatch_file_suffix"}}
                  </Text>
                </div>
              {{/if}}
            {{/if}}
            {{#if this.isReviewPanelOpen}}
              <div
                class="tio-loan-standing-attention__panel tio-col-tight"
                data-test-slr-review-name-panel
              >
                <Text @weight="semibold">{{t "slr.loan_standing.attention.review_name"}}</Text>
                <Text @size="sm">{{t "slr.loan_standing.attention.review_name_explainer"}}</Text>

                {{#if @state.employerProvidedName}}
                  <div class="tio-col-extra-tight" data-test-slr-review-employer-name>
                    <Text @size="sm" @weight="semibold">
                      {{t "slr.loan_standing.attention.name_provided_by_employer"}}
                    </Text>
                    <Text @size="sm">{{@state.employerProvidedName}}</Text>
                  </div>
                {{/if}}

                {{#if @state.documentName}}
                  <div
                    class="rounded-lg border tio-border-default tio-bg-surface px-3 py-2 tio-col-extra-tight"
                    data-test-slr-review-document-name
                  >
                    <Text @size="sm" @variant="muted">
                      {{t "slr.loan_standing.attention.name_found_in_document"}}
                    </Text>
                    <Text>{{@state.documentName}}</Text>
                  </div>
                {{/if}}

                <Text @size="sm" @weight="semibold">
                  {{t "slr.loan_standing.drawer.confirm_name_prompt"}}
                </Text>
                <RadioGroup
                  @options={{this.reviewOptions}}
                  @value={{this.reviewAnswer}}
                  @onChange={{this.setReviewAnswer}}
                  data-test-slr-review-radio-group
                />
                <div class="tio-flex-tight">
                  <Button
                    @appearance="outlined"
                    @intent="default"
                    @isDisabled={{this.confirmSelection.isRunning}}
                    data-test-slr-review-cancel-button
                    {{on "click" this.closeReviewPanel}}
                  >
                    {{t "cancel"}}
                  </Button>
                  <Button
                    @intent="primary"
                    @isRunning={{this.confirmSelection.isRunning}}
                    @isDisabled={{this.isConfirmDisabled}}
                    data-test-slr-review-confirm-button
                    {{on "click" this.confirmSelection.perform}}
                  >
                    {{t "slr.loan_standing.attention.review_confirm"}}
                  </Button>
                </div>
              </div>
            {{else if this.showContactSupportAction}}
              {{! Hard-ineligible is a statement, not a task: no Required Documents header/specs/row — the subtitle says why, the only affordance is support. }}
              <div
                class="tio-loan-standing-attention__doc-row"
                data-test-slr-loan-standing-support-row
              >
                <Text @size="sm" @variant="muted">
                  {{t "slr.loan_standing.attention.support_prompt"}}
                </Text>
                <LinkTo
                  @route="authenticated.contact-us"
                  class="tio-text-sm-medium tio-fg-info underline whitespace-nowrap"
                  data-test-slr-loan-standing-contact-support
                >
                  {{t "slr.loan_standing.actions.contact_support"}}
                </LinkTo>
              </div>
              {{! A hard-ineligible verdict from THIS session's upload still names the file that produced it, anchoring the banner to the upload — needs its item container here too (no document row in this branch). }}
              {{#if this.uploadFeedback}}
                <div
                  class="tio-loan-standing-attention__doc-item {{this.docItemBorderClass}}"
                  data-test-slr-loan-standing-support-feedback-item
                >
                  <UploadFeedbackBanner
                    @feedback={{this.uploadFeedback}}
                    @onDismiss={{this.dismissUploadFeedback}}
                    @onOpenLeaveStatus={{this.openLeaveStatusModal}}
                  />
                </div>
              {{/if}}
            {{else}}
              {{#unless this.isNameConfirmVariant}}
                <div class="tio-row-tight">
                  <Text @weight="semibold">
                    {{t "slr.loan_standing.attention.required_documents"}}
                  </Text>
                  <Text @size="sm" @variant="muted">
                    {{t "slr.loan_standing.attention.supported_format"}}
                  </Text>
                </div>
              {{/unless}}
              {{! One bordered item per document (Figma 767-7670): the row line and realtime feedback nest INSIDE it, never as sibling boxes; the item also doubles as the drag-and-drop target. }}
              <FileDropzone
                @queue={{this.dropzoneQueue}}
                @multiple={{false}}
                class="tio-loan-standing-attention__doc-item {{this.docItemBorderClass}}"
                data-test-slr-loan-standing-doc-item
                as |dropzone|
              >
                {{! The drop invitation is suppressed mid-verification — handleFileAdded discards those drops, so promising an upload the card won't perform would be a lie. }}
                <div
                  class="tio-loan-standing-attention__doc-row
                    {{if (and dropzone.active (not this.isProcessing)) 'tio-bg-info-soft'}}"
                  data-test-slr-loan-standing-doc-row
                >
                  <div class="tio-row-tight">
                    <Text>
                      {{#if (and dropzone.active (not this.isProcessing))}}
                        {{t "slr.loan_standing.attention.drop_to_upload"}}
                      {{else}}
                        {{t this.actionRowLabelKey}}
                      {{/if}}
                    </Text>
                    {{#if this.isProcessing}}
                      {{! The TA categorized-upload analyzing treatment (document-row.gts): pulsing sparkle + staggered bouncing dots. }}
                      <Chip
                        @intent="brand"
                        @appearance="faded"
                        @size="sm"
                        data-test-slr-loan-standing-doc-analyzing
                      >
                        <PhSparkle @size={{12}} class="animate-pulse" />
                        <span>{{t "slr.loan_standing.attention.analyzing"}}</span>
                        <span class="flex gap-px">
                          <span class="animate-bounce delay-0 duration-1000">.</span>
                          <span class="animate-bounce delay-200 duration-1000">.</span>
                          <span class="animate-bounce delay-400 duration-1000">.</span>
                        </span>
                      </Chip>
                    {{/if}}
                  </div>
                  {{#unless this.isProcessing}}
                    {{#if this.showUploadAction}}
                      {{! The hidden input carries the queue wiring; the visible Button opens it programmatically — selecting a file IS the submission (Figma 767-7670). }}
                      {{#let
                        (fileQueue name="slr-loan-standing-inline" onFileAdded=this.handleFileAdded)
                        as |queue|
                      }}
                        <input
                          {{queue.selectFile}}
                          {{this.registerFileInput}}
                          type="file"
                          accept={{ACCEPTED_FILE_TYPES}}
                          class="sr-only"
                          aria-hidden="true"
                          tabindex="-1"
                          data-test-slr-loan-standing-file-input
                        />
                      {{/let}}
                      <Button
                        @intent="primary"
                        @appearance="outlined"
                        @size="sm"
                        data-test-slr-loan-standing-doc-upload
                        {{on "click" this.primaryCtaAction}}
                      >
                        {{t this.uploadCtaKey}}
                      </Button>
                    {{/if}}
                  {{/unless}}
                </div>
                {{#if this.isProcessing}}
                  {{! Same left-accent strip family as the flagged banner (TA's FlaggedBanner recipe), in the info wash. }}
                  <p
                    class="border-l-4 tio-border-info tio-bg-info-soft px-4 py-3 text-sm tio-fg-secondary whitespace-normal break-words last:rounded-b-xl"
                    data-test-slr-loan-standing-processing-banner
                  >
                    {{#if this.isVerifyingPhase}}
                      {{t "slr.loan_standing.attention.verifying_file"}}
                    {{else}}
                      {{t
                        "slr.loan_standing.attention.processing_file"
                        filename=(if this.liveUpload.filename this.liveUpload.filename "")
                      }}
                    {{/if}}
                  </p>
                {{/if}}
                {{#if this.uploadFeedback}}
                  <UploadFeedbackBanner
                    @feedback={{this.uploadFeedback}}
                    @onDismiss={{this.dismissUploadFeedback}}
                    @onOpenLeaveStatus={{this.openLeaveStatusModal}}
                  />
                {{/if}}
                {{#each this.redirectedVerdictDisplays as |message|}}
                  <div
                    class="tio-row-top border-l-4 px-4 py-3 text-sm tio-border-warning tio-bg-warning-soft last:rounded-b-xl"
                    data-test-slr-loan-standing-redirected-verdict
                  >
                    <PhWarningCircle
                      @size={{18}}
                      @weight="fill"
                      class="mt-0.5 shrink-0 tio-fg-warning"
                    />
                    <Text @size="sm" class="tio-fg-warning-body">{{message}}</Text>
                  </div>
                {{/each}}
              </FileDropzone>
              {{#if this.isNsldsVariant}}
                <div
                  class="tio-loan-standing-attention__doc-row tio-loan-standing-attention__doc-row--accepted"
                  data-test-slr-loan-standing-first-doc-row
                >
                  <Text>{{t "slr.loan_standing.attention.loan_verification_document"}}
                    <span class="sr-only">{{t "slr.loan_standing.attention.verified"}}</span></Text>
                  <span class="tio-loan-standing-attention__accepted-icon" aria-hidden="true">
                    <PhCheckCircle @size={{20}} @weight="duotone" />
                  </span>
                </div>
              {{/if}}
            {{/if}}
          </div>
        </div>
        {{! Leave-status pop-up (Figma 767-20511): why a fresh NSLDS export may
            not exist, and what to upload instead. }}
        <Modal
          @isOpen={{this.isLeaveStatusModalOpen}}
          @onClose={{this.closeLeaveStatusModal}}
          @size="md"
          @ariaLabel={{t "slr.loan_standing.attention.leave_status_title"}}
          data-test-slr-leave-status-modal
        >
          <:header>
            <Text @weight="semibold">
              {{t "slr.loan_standing.attention.leave_status_title"}}
            </Text>
          </:header>
          <:body>
            <div class="tio-col-tight">
              <Text @size="sm">{{t "slr.loan_standing.attention.leave_status_intro"}}</Text>
              <ul class="list-disc pl-6 tio-col-extra-tight">
                <li><Text @size="sm">
                    {{t "slr.loan_standing.attention.leave_status_deferment"}}</Text></li>
                <li><Text @size="sm">
                    {{t "slr.loan_standing.attention.leave_status_grace"}}</Text></li>
                <li><Text @size="sm">
                    {{t "slr.loan_standing.attention.leave_status_forbearance"}}</Text></li>
                <li><Text @size="sm">
                    {{t "slr.loan_standing.attention.leave_status_in_school"}}</Text></li>
              </ul>
              <Text @size="sm">{{t "slr.loan_standing.attention.leave_status_outro"}}</Text>
            </div>
          </:body>
        </Modal>
      </Card>
    {{/if}}
  </template>
}
